import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeLang } from '../../../utils/localeDataLoader';
import { getOrganizationCharts } from '../../../apis/leaderCategories';
import imgToChuc from '../../../images/sodotochuc.png';
import { FiShield, FiBriefcase } from 'react-icons/fi';
import '../ecosystem/ecosystem-detail.scss';
import ThuongMaiOrgChart from './ThuongMaiOrgChart';
import HsvOrgChartReactFlow from '../ecosystem/react-flow/HsvOrgChartReactFlow';
import HsvMatrixReactFlow from '../ecosystem/react-flow/HsvMatrixReactFlow';
import HsvPyramidReactFlow from '../ecosystem/react-flow/HsvPyramidReactFlow';

function LeaderOrganize() {
    const { i18n } = useTranslation();
    const isEnglish = i18n.language.startsWith('en');
    const [activeTab, setActiveTab] = useState('corp'); // 'corp' | 'thuongmai'
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCharts = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const lang = normalizeLang(i18n.language);
                const result = await getOrganizationCharts(lang);
                setData(result);
            } catch (err) {
                setError(err);
                setData([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCharts();
    }, [i18n.language]);


    const pyramidLayers = isEnglish
        ? [
            { 
                name: 'Board of Directors - BOD', 
                role: 'Chairman', 
                color: 'blue',
                descriptions: [
                    'Central Function: Strategic leadership and control',
                    'Practice good governance of Specialized Councils'
                ]
            },
            { 
                name: 'Executive Board - BOE', 
                role: 'CEO', 
                color: 'orange',
                descriptions: [
                    'Day-to-day strategic management',
                    'Specialized operational divisions by branch: Hospital, specialty, clinic',
                    'Centralized management, supervision, support, and processing divisions/departments'
                ]
            },
            { 
                name: 'Functional Divisions', 
                roles: ['Specialized Divisions', 'Support Divisions/Depts'], 
                color: 'gray'
            },
            { 
                name: 'Affiliated Companies (Hospitals & BU)', 
                roles: ['General Hospital', 'Specialized Hospital', 'Clinics', 'Pharmacies', 'Medical School'], 
                color: 'yellow',
                descriptions: [
                    'Affiliated Hospital Companies',
                    'Pharmaceutical Co. (Corporation model), 1 Commercial - Service Co. providing in-hospital services (laundry, food, supermarket, cleaning, parking,...)',
                    '1 medical nursing training school (Cooperation)'
                ]
            }
        ]
        : [
            { 
                name: 'Hội đồng Quản trị - BOD', 
                role: 'Chủ tịch', 
                color: 'blue',
                descriptions: [
                    'Chức năng Trung tâm: Lãnh đạo và kiểm soát chiến lược',
                    'Thực hành thông lệ quản trị tốt Các Hội đồng chuyên môn'
                ]
            },
            { 
                name: 'Ban Điều Hành - BOE', 
                role: 'CEO', 
                color: 'orange',
                descriptions: [
                    'Điều hành chiến lược hàng ngày',
                    'Các khối vận hành chuyên môn theo từng nhánh: Bệnh viện, chuyên khoa, phòng khám',
                    'Các khối/phòng quản lý, giám sát, hỗ trợ, xử lý tập trung'
                ]
            },
            { 
                name: 'Các Khối Chức Năng', 
                roles: ['Các khối chuyên môn', 'Khối/Phòng Hỗ trợ'], 
                color: 'gray' 
            },
            { 
                name: 'Các công ty trực thuộc (Hospitals & BU)', 
                roles: ['BV ĐK', 'BV chuyên khoa', 'Phòng khám', 'Nhà thuốc', 'Trường ĐTYK'], 
                color: 'yellow',
                descriptions: [
                    'Các Công ty BV trực thuộc',
                    'Cty Dược (mô hình Tổng công ty), 1 Cty Thương mại - Dịch vụ cung cấp các dịch vụ trong bệnh viện (giặt ủi, ăn uống, siêu thị, vệ sinh, giữ xe,...)',
                    '1 trường đào tạo điều dưỡng y tế (Liên kết hợp tác)'
                ]
            }
        ];

    return (
        <div style={{ paddingBottom: '40px' }}>
            {/* Tabs for switching between HSV-Corp and HSV-Thương Mại */}
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '40px'
            }}>
                <div style={{
                    display: 'inline-flex',
                    background: 'rgba(77, 183, 72, 0.08)',
                    padding: '6px',
                    borderRadius: '50px',
                    border: '2px solid rgb(77, 183, 72)',
                    boxShadow: '0 4px 20px rgba(77, 183, 72, 0.15)'
                }}>
                    <button
                        type="button"
                        onClick={() => setActiveTab('corp')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 32px',
                            borderRadius: '40px',
                            fontWeight: 700,
                            fontSize: '1.15rem',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            backgroundColor: activeTab === 'corp' ? 'rgb(77, 183, 72)' : 'transparent',
                            color: activeTab === 'corp' ? '#ffffff' : 'rgb(77, 183, 72)',
                            boxShadow: activeTab === 'corp' ? '0 6px 18px rgba(77, 183, 72, 0.4)' : 'none',
                            transform: activeTab === 'corp' ? 'scale(1.02)' : 'scale(1)',
                        }}
                    >
                        <FiShield style={{ fontSize: '1.3rem' }} />
                        <span>HSV CORP</span>
                    </button>

                    <button
                        type="button"
                        onClick={() => setActiveTab('thuongmai')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 32px',
                            borderRadius: '40px',
                            fontWeight: 700,
                            fontSize: '1.15rem',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            backgroundColor: activeTab === 'thuongmai' ? 'rgb(77, 183, 72)' : 'transparent',
                            color: activeTab === 'thuongmai' ? '#ffffff' : 'rgb(77, 183, 72)',
                            boxShadow: activeTab === 'thuongmai' ? '0 6px 18px rgba(77, 183, 72, 0.4)' : 'none',
                            transform: activeTab === 'thuongmai' ? 'scale(1.02)' : 'scale(1)',
                        }}
                    >
                        <FiBriefcase style={{ fontSize: '1.3rem' }} /> <span>{isEnglish ? 'HSV COMMERCE' : 'HSV-Thương Mại'}</span>
                    </button>
                </div>
            </div>

            {activeTab === 'corp' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '40px', margin: '20px auto' }}>
                    <HsvPyramidReactFlow isEnglish={isEnglish} />
                    
                    <div style={{ textAlign: 'center' }}>
                        <h2 className="hsv-chart-section-title" style={{ marginTop: '3rem' }}>
                            {isEnglish ? 'Functional & Professional Management Chart' : 'Sơ Đồ Chức Năng & Quản Lý Chuyên Môn'}
                        </h2>
                        <HsvOrgChartReactFlow isEnglish={isEnglish} />
                    </div>

                    <div style={{ textAlign: 'center' }}>
                        <h2 className="hsv-chart-section-title" style={{ marginTop: '3rem' }}>
                            {isEnglish ? 'Joint Stock Company Structural Form' : 'Hình Thức Cấu Trúc Công Ty Cổ Phần'}
                        </h2>
                        <HsvMatrixReactFlow isEnglish={isEnglish} />
                    </div>
                </div>
            ) : (
                <div style={{ margin: '20px auto', maxWidth: '1200px' }}>
                    <ThuongMaiOrgChart />
                </div>
            )}
        </div>
    );
}

export default LeaderOrganize;
